/**
 * ToolService - Service for fetching tool configurations from backend
 * Provides dynamic tool discovery without hardcoded definitions
 */

const API_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_APIURLFull || 'https://api.krh1996.com';

export class ToolService {
  static toolsCache = null;
  static cacheTimestamp = null;
  static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch all available tools from backend
   * @param {boolean} clearCache - Force refresh by clearing cache
   * @returns {Promise<Array>} Array of tool configurations
   */
  static async getAvailableTools(clearCache = false) {
    try {
      // Clear cache if requested
      if (clearCache) {
        this.toolsCache = null;
        this.cacheTimestamp = null;
      }

      // Check if we have valid cached data
      const now = Date.now();
      if (this.toolsCache && this.cacheTimestamp && (now - this.cacheTimestamp < this.CACHE_DURATION)) {
        return this.toolsCache;
      }

      // Fetch fresh data from backend
      const response = await fetch(`${API_URL}/api/tools`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch tools');
      }
      
      // Update cache
      this.toolsCache = data.tools;
      this.cacheTimestamp = now;
      
      return data.tools;
    } catch (error) {
      console.error('[ToolService] Error fetching tools:', error);
      throw error;
    }
  }

  /**
   * Clear the tools cache
   */
  static clearCache() {
    this.toolsCache = null;
    this.cacheTimestamp = null;
  }

  /**
   * Fetch specific tool by name
   * @param {string} toolName - Name of the tool
   * @returns {Promise<Object>} Tool configuration
   */
  static async getTool(toolName) {
    try {
      const response = await fetch(`${API_URL}/api/tools/${toolName}`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Tool not found');
      }
      
      return data.tool;
    } catch (error) {
      console.error(`[ToolService] Error fetching tool '${toolName}':`, error);
      throw error;
    }
  }

  /**
   * Fetch tools by category
   * @param {string} category - Category name
   * @returns {Promise<Array>} Array of tools in category
   */
  static async getToolsByCategory(category) {
    try {
      const response = await fetch(`${API_URL}/api/tools/categories/${category}`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch category');
      }
      
      return data.tools;
    } catch (error) {
      console.error(`[ToolService] Error fetching category '${category}':`, error);
      throw error;
    }
  }

  /**
   * Get tools that require API keys
   * @param {Array} tools - Array of tool configurations
   * @returns {Array} Tools that require API keys
   */
  static getToolsRequiringApiKeys(tools) {
    return tools.filter(tool => tool.requiresApiKey?.required);
  }

  /**
   * Get required API key providers from tool list
   * @param {Array} tools - Array of tool configurations
   * @returns {Array} Array of unique provider names
   */
  static getRequiredApiKeyProviders(tools) {
    return [...new Set(
      tools
        .filter(tool => tool.requiresApiKey?.required)
        .map(tool => tool.requiresApiKey.provider)
    )];
  }

  /**
   * Check if a tool requires an API key
   * @param {Object} tool - Tool configuration
   * @returns {boolean} True if tool requires API key
   */
  static toolRequiresApiKey(tool) {
    return tool.requiresApiKey?.required === true;
  }

  /**
   * Get API key provider for a tool
   * @param {Object} tool - Tool configuration
   * @returns {string|null} Provider name or null
   */
  static getToolApiKeyProvider(tool) {
    return tool.requiresApiKey?.provider || null;
  }
}

